'use strict';

var CallsDevice = require('./util/CallsDevice');
var CmdDevice = require('./util/CmdDevice');
var IdGenerator = require('./util/IdGenerator');
var log = require('../../../log/Log').channel('Simulator');
var calendarEvents = require('./util/SimulatorCalendarEvents.json');
var directoryEntries = process.env.simDir ?
    require(`${process.env.simDir}`) :
    require('./util/SimulatorDirectoryEntries.json');

var copyProps = [
    'configName',
    'defaultCallCanDial',
    'defaultCallCanEndMeeting',
];

function Simulator(driver, ipAddress, username, password, onConnectStatus,
    onControlStatus, config, getTranslation) {

    this.name = driver.prototype.name;
    this.simulationParams = driver.prototype.simulationParams;
    this.failOnEmptyConfig = driver.prototype.failOnEmptyConfig;
    this.getTranslation = getTranslation;

    for (var i = 0; i < copyProps.length; i++) {
        if (driver.prototype.hasOwnProperty(copyProps[i])) {
            this[copyProps[i]] = driver.prototype[copyProps[i]];
        }
    }

    if (driver.prototype.hasOwnProperty('onCallCanDial')) {
        this.onCallCanDial = driver.prototype.onCallCanDial.bind(this);
    }

    if (this.simulationParams.hasOwnProperty('caps')) {
        if (this.simulationParams.caps.hasCalendar === true) {
            this.initializeCalendarEvents();
        }
        if (this.simulationParams.caps.hasDirectory === true) {
            this.initializeDirectoryEntries();
        }
    }

    this.states = this.getNode(config, [ this.configName, 'states' ], {});

    this.simulate = true;
    this.power = false;
    this.volume = 12;
    this.volumeMute = false;
    this.mute = false;
    this.assistiveListening = {};
    this.assistiveListeningMute = false;
    this.assistiveListeningVolume = 0;
    this.overflowRooms = {};
    this.soundReinforcementZones = {};
    this.contentSource = null;
    this.videoInput = null;
    this.lastCameraPreset = null;
    this.videoMatrixRoutes = {};
    this.cameras = {};
    this.audioInputSources = {};
    this.layouts = {};
    this.layoutSelections = {};
    this.updateStatesTimeout = null;
    this.updateStatesAfterTimeout = false;
    this.myParticipantId = this.generateId();
    this.displayBlank = false;
    this.displayFreeze = false;

    CallsDevice.call(this);
    CmdDevice.call(this, ipAddress, username, password, onConnectStatus,
        onControlStatus, config);
}

Simulator.prototype = Object.create(CmdDevice.prototype);
Simulator.prototype.constructor = Simulator;

Object.assign(Simulator.prototype, CallsDevice.prototype);

Simulator.prototype.name = 'Simulator';
Simulator.prototype.key = 'simulator';

Simulator.prototype.commands = {};

Simulator.prototype.commands.answer = {};
Simulator.prototype.commands.answer.set = function(callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error('answer: invald callId ' + callId);
        return;
    }

    this.answerCall(ci);
};

Simulator.prototype.commands.assistiveListeningMute = {};
Simulator.prototype.commands.assistiveListeningMute.get = function() {
    this.broadcastStatus('assistiveListeningMute', this.assistiveListeningMute);
};
Simulator.prototype.commands.assistiveListeningMute.set = function(value) {
    this.assistiveListeningMute = value;
    this.broadcastStatus('assistiveListeningMute', this.assistiveListeningMute);
};

Simulator.prototype.commands.assistiveListeningVolume = {};
Simulator.prototype.commands.assistiveListeningVolume.get = function() {
    this.broadcastStatus('assistiveListeningVolume', this.assistiveListeningVolume);
};
Simulator.prototype.commands.assistiveListeningVolume.set = function(value) {
    this.assistiveListeningVolume = value;
    this.broadcastStatus('assistiveListeningVolume', this.assistiveListeningVolume);
};

Simulator.prototype.commands.calendar = {};
Simulator.prototype.commands.calendar.get = function() {
    this.broadcastStatus('calendar', { events: this.calendar });
};

Simulator.prototype.commands.call = {};
Simulator.prototype.commands.call.set = function(value) {
    var ci = this.getAvailableCallInterface(value.callType);
    var dialString = value.dialString;
    var canManageParticipants =
        this.getNode(this.simulationParams, [ 'caps', 'canManageParticipants' ], false);

    if (ci === null) {
        log.error('call: no ' + value.callType + ' resources available');
        return;
    }

    if (typeof dialString === 'undefined' &&
        this.simulationParams.hasOwnProperty('defaultDialString')) {
        dialString = this.simulationParams.defaultDialString;
    }

    this.placeCall(ci, value.callId, dialString);

    if (canManageParticipants) {
        this.participants = {};
        this.participants[this.myParticipantId] = {
            audioMuted: this.mute,
            displayName: 'My Conference Room',
            hasAudioMute: true,
            hasVideoMute: true,
            isCohost: false,
            isHost: true,
            isMe: true,
            sendingVideo: this.sendingVideo,
            userId: '0',
            userIdType: 'zoom',
        };

        if (value.hasOwnProperty('invite')) {
            for (var id in value.invite) {
                if (!value.invite.hasOwnProperty(id)) continue;
                var contact = this.findDirectoryEntry(value.invite[id]);
                if (contact === null) {
                    log.error(this + ': failed looking up directory entry for: ' + value.invite[id]);
                    continue;
                }
                this.participants[this.generateId()] = {
                    audioMuted: false,
                    displayName: this.makeParticipantDisplayName(contact),
                    hasAudioMute: true,
                    hasVideoMute: true,
                    isCohost: false,
                    isHost: false,
                    sendingVideo: true,
                    userId: value.invite[id],
                    userIdType: 'zoom',
                };
            }
        }

        this.broadcastStatus('callParticipants', {
            callId: value.callId,
            participants: this.participants,
        });
    }

    window.setTimeout((function() {
        if (this.fsm.current === 'controlling' &&
            ci.hasOwnProperty('callStatus') &&
            ci.callStatus.callState === 'ringing') {
            if (this.simulationParams.hasOwnProperty('callStatus')) {
                for (var propName in this.simulationParams.callStatus) {
                    if (this.simulationParams.callStatus.hasOwnProperty(propName)) {
                        ci.callStatus[propName] = this.simulationParams.callStatus[propName];
                    }
                }
            }
            if (this.simulationParams.hasOwnProperty('formatDialString') &&
                typeof this.simulationParams.formatDialString === 'function') {
                var contactName; // undefined
                this.connectCall(ci, null, dialString, contactName,
                    this.simulationParams.formatDialString.call(this, dialString));
            } else {
                this.connectCall(ci);
            }
        }
    }).bind(this), 1000);
};

Simulator.prototype.commands.callLayout = {};
Simulator.prototype.commands.callLayout.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error('callLayout: invalid callId ' + value.callId);
        return;
    }

    this.updateCallProperty(ci, [ 'layouts', 'current' ], value.value);
};

Simulator.prototype.commands.callStatus = {};
Simulator.prototype.commands.callStatus.get = function() {
    this.sendCallStatusMessages();
};

Simulator.prototype.commands.cameraMove = {};
Simulator.prototype.commands.cameraMove.set = function(value) {
    if (!this.cameras.hasOwnProperty(value.cameraId)) {
        log.error('cameraMove: no camera with id ' + value.cameraId);
        return;
    }

    if (!value.hasOwnProperty('value')) return;

    if (this.lastCameraPreset !== null) {
        this.lastCameraPreset = null;
        this.broadcastStatus('cameraPreset', this.lastCameraPreset);
    }

    this.cameras[value.cameraId].position.x += value.value.x;
    this.cameras[value.cameraId].position.y += value.value.y;
    this.cameras[value.cameraId].position.z += value.value.z;
};

Simulator.prototype.commands.cameraPosition = {};
Simulator.prototype.commands.cameraPosition.set = function(value) {
    this.setNode(this.states, [ 'responses', value.requestId ], {
        type: 'cameraPosition',
        cameraId: value.cameraId,
        pan: 255,
        tilt: 255,
        zoom: 255,
    });
    this.sendStates(this.states);
};

Simulator.prototype.commands.cameraPreset = {};
Simulator.prototype.commands.cameraPreset.get = function() {
    this.broadcastStatus('cameraPreset', this.lastCameraPreset);
};
Simulator.prototype.commands.cameraPreset.set = function(value) {
    this.lastCameraPreset = value;
    this.broadcastStatus('cameraPreset', this.lastCameraPreset);
};

Simulator.prototype.commands.cameraSelected = {};
Simulator.prototype.commands.cameraSelected.get = function() {
    for (var cameraId in this.cameras) {
        if (!this.cameras.hasOwnProperty(cameraId)) continue;
        this.broadcastStatus('cameraSelected', {
            cameraId: cameraId,
            value: this.cameras[cameraId].selected,
        });
    }
};
Simulator.prototype.commands.cameraSelected.set = function(cameraId) {
    if (!this.cameras.hasOwnProperty(cameraId)) {
        log.error('cameraSelected: no camera with id ' + cameraId);
        return;
    }

    for (var id in this.cameras) {
        if (!this.cameras.hasOwnProperty(id)) continue;

        if (id === cameraId) {
            this.cameras[cameraId].selected = true;
            this.broadcastStatus('cameraSelected', {
                cameraId: cameraId,
                value: this.cameras[cameraId].selected,
            });
            this.updateStatesIfNecessary('cameraSelected', cameraId);
        } else if (this.cameras[id].selected) {
            this.cameras[id].selected = false;
            this.broadcastStatus('cameraSelected', {
                cameraId: id,
                value: this.cameras[id].selected,
            });
        }
    }
};

Simulator.prototype.commands.cameraTracking = {};
Simulator.prototype.commands.cameraTracking.get = function() {
    for (var cameraId in this.cameras) {
        if (!this.cameras.hasOwnProperty(cameraId)) continue;
        this.broadcastStatus('cameraTracking', {
            cameraId: cameraId,
            value: this.cameras[cameraId].tracking,
        });
    }
};
Simulator.prototype.commands.cameraTracking.set = function(value) {
    if (!this.cameras.hasOwnProperty(value.cameraId)) {
        log.error('no camera with id ' + value.cameraId);
        return;
    }

    this.cameras[value.cameraId].tracking = value.value;

    this.broadcastStatus('cameraTracking', {
        cameraId: value.cameraId,
        value: this.cameras[value.cameraId].tracking,
    });
};

Simulator.prototype.commands.dial = {};
Simulator.prototype.commands.dial.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error('dial: invalid callId ' + value.callId);
        return;
    }

    this.dialCall(ci, value.dialString);
};

Simulator.prototype.commands.directory = {};
Simulator.prototype.commands.directory.get = function() {
    this.broadcastStatus('directory', this.directory);
};

Simulator.prototype.commands.firmwareVersion = {};
Simulator.prototype.commands.firmwareVersion.get = function() {
    if (this.simulationParams.hasOwnProperty('firmwareVersion')) {
        this.broadcastStatus('firmwareVersion', this.simulationParams.firmwareVersion);
    }
};

Simulator.prototype.commands.hangup = {};
Simulator.prototype.commands.hangup.set = function(value) {
    var callId = typeof value === 'string' ? value : value.callId;
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error('hangup: invalid callId ' + callId);
        return;
    }

    if (this.sharingOnlyMeetings[ci.name]) {
        this.stopContent();
        delete this.sharingOnlyMeetings[ci.name];
    }

    this.endCall(ci);

    window.setTimeout((function() {
        if (this.fsm.current === 'controlling' &&
            ci.hasOwnProperty('callStatus') &&
            ci.callStatus.callState === 'disconnecting') {
            this.disconnectCall(ci);
        }
    }).bind(this), 1000);
};

Simulator.prototype.commands.inviteToCall = {};
Simulator.prototype.commands.inviteToCall.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error(this + ': inviteToCall.set: invalid callId ' + value.callId);
        return;
    }

    for (var id in value.invitees) {
        if (!value.invitees.hasOwnProperty(id)) continue;
        var invitee = value.invitees[id];
        var contact = this.findDirectoryEntry(invitee.address);

        if (contact === null) {
            log.error(this + ': failed looking up directory entry for: ' + invitee.address);
            continue;
        }

        var participantId = this.generateId();

        this.participants[participantId] = {
            audioMuted: false,
            displayName: this.makeParticipantDisplayName(contact),
            hasAudioMute: true,
            hasVideoMute: true,
            isCohost: false,
            isHost: false,
            sendingVideo: true,
            userId: invitee.address,
            userIdType: 'zoom',
        };

        this.broadcastStatus('callParticipant', {
            callId: value.callId,
            participantId: participantId,
            participant: this.participants[participantId],
        });
    }
};

Simulator.prototype.commands.layout = {};
Simulator.prototype.commands.layout.get = function() {
    this.broadcastStatus('layout', this.layoutSelections);
};
Simulator.prototype.commands.layout.set = function(value) {
    if (!this.layouts.hasOwnProperty(value.switcherId)) {
        log.error(this + ': layout: invalid switcherId: ' +
            JSON.stringify(value, null, 4));
        return;
    }

    if (!this.layouts[value.switcherId].hasOwnProperty(value.layoutId)) {
        log.error(this + ': layout: invaid layoutId: ' +
            JSON.stringify(value, null, 4));
        return;
    }

    this.layoutSelections[value.switcherId] = value.layoutId;
    this.broadcastStatus('layout', this.layoutSelections);
};

Simulator.prototype.commands.layouts = {};
Simulator.prototype.commands.layouts.get = function() {
    this.broadcastStatus('layouts', this.layouts);
};

Simulator.prototype.commands.mute = {};
Simulator.prototype.commands.mute.get = function() {
    this.broadcastStatus('mute', this.mute);
};
Simulator.prototype.commands.mute.set = function(value) {
    this.mute = value;
    this.broadcastStatus('mute', this.mute);

    if (this.getNode(this.simulationParams, [ 'caps', 'canManageParticipants' ])) {
        this.callInterfaces.forEach(function(ci) {
            if (this.getNode(ci, [ 'callStatus', 'callState' ]) === 'connected') {
                this.participants[this.myParticipantId].audioMuted = value;
                this.broadcastStatus('callParticipant', {
                    callId: ci.callStatus.callId,
                    participantId: this.myParticipantId,
                    participant: this.participants[this.myParticipantId],
                });
            }
        }, this);
    }
};

Simulator.prototype.commands.muteParticipant = {};
Simulator.prototype.commands.muteParticipant.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error('muteParticipant: invalid callId ' + value.callId);
        return;
    }

    if (value.participantId === '*') {
        for (var participantId in this.participants) {
            if (!this.participants.hasOwnProperty(participantId)) continue;
            if (participantId === this.myParticipantId) continue;
            this.participants[participantId].audioMuted = value.value;
        }

        this.broadcastStatus('callParticipants', {
            callId: value.callId,
            participants: this.participants,
        });
    } else {
        if (!this.participants.hasOwnProperty(value.participantId)) {
            log.error('muteParticipant: invalid participantId ' + value.participantId);
            return;
        }

        this.participants[value.participantId].audioMuted = value.value;
        this.broadcastStatus('callParticipant', {
            callId: value.callId,
            participantId: value.participantId,
            participant: this.participants[value.participantId],
        });
    }
};

Simulator.prototype.commands.muteParticipantVideo = {};
Simulator.prototype.commands.muteParticipantVideo.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error('muteParticipantVideo: invalid callId ' + value.callId);
        return;
    }

    if (value.participantId === '*') {
        for (var participantId in this.participants) {
            if (!this.participants.hasOwnProperty(participantId)) continue;
            if (participantId === this.myParticipantId) continue;
            this.participants[participantId].sendingVideo = !value.value;
        }

        this.broadcastStatus('callParticipants', {
            callId: value.callId,
            participants: this.participants,
        });
    } else {
        if (!this.participants.hasOwnProperty(value.participantId)) {
            log.error('muteParticipantVideo: invalid participantId ' + value.participantId);
            return;
        }

        this.participants[value.participantId].sendingVideo = !value.value;
        this.broadcastStatus('callParticipant', {
            callId: value.callId,
            participantId: value.participantId,
            participant: this.participants[value.participantId],
        });
    }
};

Simulator.prototype.commands.audioInputMute = {};
Simulator.prototype.commands.audioInputMute.get = function() {
    for (var srcId in this.audioInputSources) {
        if (!this.audioInputSources.hasOwnProperty(srcId)) continue;
        this.broadcastStatus('audioInputMute', {
            srcId: srcId,
            value: this.audioInputSources[srcId].mute,
        });
    }
};
Simulator.prototype.commands.audioInputMute.set = function(value) {
    if (!this.config.hasOwnProperty('audioInputSources') ||
        !this.config.audioInputSources.hasOwnProperty(value.srcId)) {
        log.error('no audio input source with srcId ' + value.srcId +
            ' in config = ' + JSON.stringify(this.config, null, 4));
        return;
    }

    this.audioInputSources[value.srcId].mute = value.value;

    this.broadcastStatus('audioInputMute', {
        srcId: value.srcId,
        value: value.value,
    });
};

Simulator.prototype.commands.audioInputVolume = {};
Simulator.prototype.commands.audioInputVolume.get = function() {
    for (var srcId in this.audioInputSources) {
        if (!this.audioInputSources.hasOwnProperty(srcId)) continue;
        this.broadcastStatus('audioInputVolume', {
            srcId: srcId,
            value: this.audioInputSources[srcId].volume,
        });
    }
};
Simulator.prototype.commands.audioInputVolume.set = function(value) {
    if (!this.config.hasOwnProperty('audioInputSources') ||
        !this.config.audioInputSources.hasOwnProperty(value.srcId)) {
        log.error('no audio input source with srcId ' + value.srcId +
            ' in config = ' + JSON.stringify(this.config, null, 4));
        return;
    }

    this.audioInputSources[value.srcId].volume = value.value;

    this.broadcastStatus('audioInputVolume', {
        srcId: value.srcId,
        value: value.value,
    });
};

Simulator.prototype.commands.contentState = {};
Simulator.prototype.commands.contentState.get = function() {
    for (var id in this.caps.videoInputs) {
        if (!this.caps.videoInputs.hasOwnProperty(id)) continue;
        if (id !== this.noContentId) {
            this.broadcastStatus('contentState', {
                id: id,
                state: this.contentStates[id],
            });
        }
    }
};

Simulator.prototype.commands.contentSource = {};
Simulator.prototype.commands.contentSource.get = function() {
    this.broadcastStatus('contentSource', this.contentSource);
};
Simulator.prototype.commands.contentSource.set = function(value) {
    if (!this.config.hasOwnProperty('videoInputs') ||
        !(value === null || this.videoInputsConfig.hasOwnProperty(value))) {
        log.error('contentSource: videoInput ' + value +
            ' not present in config = ' + JSON.stringify(this.config, null, 4));
        return;
    }

    var stopContent = this.getNode(this.simulationParams,
        [ 'videoInputs', 'autoStartContent', this.videoInputsConfig[this.contentSource].code ],
        false);

    if (stopContent) this.stopContent(this.contentSource);

    this.contentSource = value === null ? this.noContentId : value;
    this.broadcastStatus('contentSource', this.contentSource);

    // Calling startContent when we're setting the source to noContentId
    // causes content to stop on the other inputs. Assume we can do this
    // if there are no autoStartContent entries set to false.

    if (this.contentSource === this.noContentId) {
        var autoStartContent = this.getNode(this.simulationParams,
            [ 'videoInputs', 'autoStartContent' ]);

        for (var id in autoStartContent) {
            if (!autoStartContent.hasOwnProperty(id)) continue;

            if (autoStartContent[id] === false) return;
        }
    }

    var startContent = this.getNode(this.simulationParams,
        [ 'videoInputs', 'autoStartContent', this.videoInputsConfig[value].code ], true);

    if (startContent) this.startContent(value);
};

Simulator.prototype.commands.displayBlank = {};
Simulator.prototype.commands.displayBlank.get = function() {
    this.broadcastStatus('displayBlank', this.displayBlank);
};
Simulator.prototype.commands.displayBlank.set = function(value) {
    this.displayBlank = value;
    this.broadcastStatus('displayBlank', value);
};

Simulator.prototype.commands.displayFreeze = {};
Simulator.prototype.commands.displayFreeze.get = function() {
    this.broadcastStatus('displayFreeze', this.displayFreeze);
};
Simulator.prototype.commands.displayFreeze.set = function(value) {
    this.displayFreeze = value;
    this.broadcastStatus('displayFreeze', value);
};

Simulator.prototype.commands.equipmentSetting = {};
Simulator.prototype.commands.equipmentSetting.set = function(value) {
    var info = this.getNode(this,
        ['simulationParams', 'equipmentSettings', value.name]);

    if (info === null) {
        log.error('no definition for "' + value.name + '" equipment setting');
        return;
    }

    if (info.hasOwnProperty('stateName')) {
        this.states[info.stateName] = value.value;
        this.updateStatesDeferred();
    }

    switch (info.type) {
        case 'dummy':
            break;
        case 'commandMap':
            this.set(info.cmdName,
                info.convertToCommand.call(this, value.value));
            break;
        default:
            log.error('unrecognized type "' + info.type + '" for "' +
                value.name + '" equipment setting');
            break;
    }
};

Simulator.prototype.commands.overflowRoomMute = {};
Simulator.prototype.commands.overflowRoomMute.get = function() {
    for (var roomId in this.overflowRooms) {
        if (!this.overflowRooms.hasOwnProperty(roomId)) continue;
        this.broadcastStatus('overflowRoomMute', {
            roomId: roomId,
            value: this.overflowRooms[roomId].mute,
        });
    }
};
Simulator.prototype.commands.overflowRoomMute.set = function(value) {
    if (!this.overflowRooms.hasOwnProperty(value.roomId)) {
        log.error('no overflow room with roomId ' + value.roomId +
            ' in config = ' + JSON.stringify(this.config, null, 4));
        return;
    }

    this.overflowRooms[value.roomId].mute = value.value;

    this.broadcastStatus('overflowRoomMute', {
        roomId: value.roomId,
        value: this.overflowRooms[value.roomId].mute,
    });
};

Simulator.prototype.commands.overflowRoomVolume = {};
Simulator.prototype.commands.overflowRoomVolume.get = function() {
    for (var roomId in this.overflowRooms) {
        if (!this.overflowRooms.hasOwnProperty(roomId)) continue;
        this.broadcastStatus('overflowRoomVolume', {
            roomId: roomId,
            value: this.overflowRooms[roomId].volume,
        });
    }
};
Simulator.prototype.commands.overflowRoomVolume.set = function(value) {
    if (!this.overflowRooms.hasOwnProperty(value.roomId)) {
        log.error('no overflow room with roomId ' + value.roomId +
            ' in config = ' + JSON.stringify(this.config, null, 4));
        return;
    }

    this.overflowRooms[value.roomId].volume = value.value;

    this.broadcastStatus('overflowRoomVolume', {
        roomId: value.roomId,
        value: this.overflowRooms[value.roomId].volume,
    });
};

Simulator.prototype.commands.power = {};
Simulator.prototype.commands.power.get = function() {
    this.broadcastStatus('power', this.power);
};
Simulator.prototype.commands.power.set = function(value) {
    this.power = value;
    this.broadcastStatus('power', this.power);
};

Simulator.prototype.commands.receiveGain = {};
Simulator.prototype.commands.receiveGain.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error('receiveGain: invalid callId ' + value.callId);
        return;
    }

    if (!ci.hasOwnProperty('receiveGainValue')) {
        log.error('receiveGain: not supported for callId = ' + value.callId);
        return;
    }

    this.updateReceiveGain(ci, value.value);
};

Simulator.prototype.commands.reject = {};
Simulator.prototype.commands.reject.set = function(callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error('reject: invalid callId ' + callId);
        return;
    }

    this.endCall(ci);

    window.setTimeout((function() {
        if (this.fsm.current === 'controlling') {
            this.disconnectCall(ci);
        }
    }).bind(this), 1000);
};

Simulator.prototype.commands.sendVideo = {};
Simulator.prototype.commands.sendVideo.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error(this + ': sendVideo: invalid callId: ' + value.callId);
        return;
    }

    this.sendingVideo = value.value;
    ci.callStatus.sendingVideo = this.sendingVideo;
    this.broadcastStatus('callStatus', JSON.parse(JSON.stringify(ci.callStatus)));

    if (this.getNode(this.simulationParams, [ 'caps', 'canManageParticipants' ])) {
        this.participants[this.myParticipantId].sendingVideo = this.sendingVideo;
        this.broadcastStatus('callParticipant', {
            callId: value.callId,
            participantId: this.myParticipantId,
            participant: this.participants[this.myParticipantId],
        });
    }
};

Simulator.prototype.commands.soundReinforcementMute = {};
Simulator.prototype.commands.soundReinforcementMute.get = function() {
    for (var zoneId in this.soundReinforcementZones) {
        if (!this.soundReinforcementZones.hasOwnProperty(zoneId)) continue;
        this.broadcastStatus('soundReinforcementMute', {
            zoneId: zoneId,
            value: this.soundReinforcementZones[zoneId].mute,
        });
    }
};
Simulator.prototype.commands.soundReinforcementMute.set = function(value) {
    if (!this.soundReinforcementZones.hasOwnProperty(value.zoneId)) {
        log.error('no sound reinforcement zone with zoneId ' + value.zoneId +
            ' in config = ' + JSON.stringify(this.config, null, 4));
        return;
    }

    this.soundReinforcementZones[value.zoneId].mute = value.value;

    this.broadcastStatus('soundReinforcementMute', {
        zoneId: value.zoneId,
        value: this.soundReinforcementZones[value.zoneId].mute,
    });
};

Simulator.prototype.commands.soundReinforcementVolume = {};
Simulator.prototype.commands.soundReinforcementVolume.get = function() {
    for (var zoneId in this.soundReinforcementZones) {
        if (!this.soundReinforcementZones.hasOwnProperty(zoneId)) continue;
        this.broadcastStatus('soundReinforcementVolume', {
            zoneId: zoneId,
            value: this.soundReinforcementZones[zoneId].volume,
        });
    }
};
Simulator.prototype.commands.soundReinforcementVolume.set = function(value) {
    if (!this.soundReinforcementZones.hasOwnProperty(value.zoneId)) {
        log.error('no sound reinforcement zone with zoneId ' + value.zoneId +
            ' in config = ' + JSON.stringify(this.config, null, 4));
        return;
    }

    this.soundReinforcementZones[value.zoneId].volume = value.value;

    this.broadcastStatus('soundReinforcementVolume', {
        zoneId: value.zoneId,
        value: this.soundReinforcementZones[value.zoneId].volume,
    });
};

Simulator.prototype.commands.stopContent = {};
Simulator.prototype.commands.stopContent.set = function(srcId) {
    this.stopContent(srcId);
};

Simulator.prototype.commands.videoInput = {};
Simulator.prototype.commands.videoInput.get = function() {
    this.broadcastStatus('videoInput', this.videoInput);
};
Simulator.prototype.commands.videoInput.set = function(value) {
    if (!this.config.hasOwnProperty('videoInputs') ||
        !this.videoInputsConfig.hasOwnProperty(value)) {
        log.error('videoInput: videoInput ' + value +
            ' not present in config = ' + JSON.stringify(this.config, null, 4));
        return;
    }

    if (this.simulationParams.caps.hasPowerControl && !this.power) {
        this.set('power', true);
    }

    this.videoInput = value;
    this.broadcastStatus('videoInput', this.videoInput);
};

Simulator.prototype.commands.videoMatrixRoute = {};
Simulator.prototype.commands.videoMatrixRoute.get = function() {
    this.broadcastStatus('videoMatrixRoute', this.videoMatrixRoutes);
};
Simulator.prototype.commands.videoMatrixRoute.set = function(value) {
    if (!this.videoMatrixRoutes.hasOwnProperty(value.videoOutputId)) {
        log.error(this + 'invalid videoOutputId = ' + value.videoOutputId);
        return;
    }

    if (!this.videoInputsConfig.hasOwnProperty(value.videoInputId)) {
        log.error(this + 'invalid videoInputId = ' + value.videoInputId);
        return;
    }

    this.videoMatrixRoutes[value.videoOutputId] = value.videoInputId;
    this.broadcastStatus('videoMatrixRoute', this.videoMatrixRoutes);
};

Simulator.prototype.commands.volume = {};
Simulator.prototype.commands.volume.get = function() {
    this.broadcastStatus('volume', this.volume);
};
Simulator.prototype.commands.volume.set = function(value) {
    this.volume = value;
    this.broadcastStatus('volume', this.volume);

    if (this.simulationParams.caps.hasRoomVolumeMute) {
        var newVolumeMute = value === 0;

        if (newVolumeMute !== this.volumeMute) {
            this.volumeMute = newVolumeMute;
            this.broadcastStatus('volumeMute', this.volumeMute);
        }
    }

    this.updateStatesIfNecessary('volume', value);
};

Simulator.prototype.commands.volumeMute = {};
Simulator.prototype.commands.volumeMute.get = function() {
    this.broadcastStatus('volumeMute', this.volumeMute);
};

Simulator.prototype.configNotRequired = function(name) {
    return this.simulationParams.hasOwnProperty('configNotRequired') &&
        this.simulationParams.configNotRequired.hasOwnProperty(name) &&
        this.simulationParams.configNotRequired[name];
};

Simulator.prototype.findDirectoryEntry = function(dialString, type) {
    for (var entryId in this.directory.entries) {
        if (!this.directory.entries.hasOwnProperty(entryId)) continue;
        var entry = this.directory.entries[entryId];
        for (var contactId in entry.contacts) {
            if (!entry.contacts.hasOwnProperty(contactId)) continue;
            var contact = entry.contacts[contactId];
            if (typeof type !== 'undefined' && type !== contact.type) continue;
            if (contact.number === dialString) return entry;
        }
    }

    return null;
};

Simulator.prototype.getCameraIdFromConfigId = function(configId) {
    var foundCameraId = null;

    for (var cameraId in this.cameras) {
        if (!this.cameras.hasOwnProperty(cameraId)) continue;
        if (this.cameras[cameraId].configId === configId) {
            foundCameraId = cameraId;
            break;
        }
    }

    return foundCameraId;
};

Simulator.prototype.initializeCalendarEvents = function() {
    // The events read in from JSON will have static start and end times so we
    // will update them to start with today's date and continue from there into
    // the future.
    //
    Simulator.prototype.calendar = JSON.parse(JSON.stringify(calendarEvents));

    var key;
    var firstEvent = null;
    var today = new Date();
    var eventDate;
    var timestamps = ['start', 'end'];

    for (key in this.calendar) {
        if (!this.calendar.hasOwnProperty(key)) continue;

        if (firstEvent === null ||
            this.calendar[key].start < firstEvent) {
            firstEvent = this.calendar[key].start;
        }
    }

    firstEvent = new Date(firstEvent);

    for (key in this.calendar) {
        if (!this.calendar.hasOwnProperty(key)) continue;

        for (var i = 0; i < timestamps.length; i++) {
            eventDate = new Date(this.calendar[key][timestamps[i]]);
            eventDate.setFullYear(
                eventDate.getFullYear() - firstEvent.getFullYear() + today.getFullYear());
            eventDate.setMonth(
                eventDate.getMonth() - firstEvent.getMonth() + today.getMonth());
            eventDate.setDate(
                eventDate.getDate() - firstEvent.getDate() + today.getDate());
            this.calendar[key][timestamps[i]] = eventDate.getTime();
        }
    }
};

Simulator.prototype.initializeDirectoryEntries = function() {
    var props;
    var propId;
    var propArray;
    var propArrayIndex;

    this.directory = {
        entries: JSON.parse(JSON.stringify(directoryEntries)),
    };

    props = this.getNode(this.simulationParams, [ 'directory', 'properties' ]);

    for (propId in props) {
        if (!props.hasOwnProperty(propId)) continue;
        this.directory[propId] = props[propId];
    }

    props = this.getNode(this.simulationParams, [ 'directory', 'contact' ]);
    if (Array.isArray(props) && props.length === 0) {
        return;
    } else if (!Array.isArray(props)) {
        propArray = [ props ];
    } else {
        propArray = props;
    }
    propArrayIndex = 0;

    for (var entryId in this.directory.entries) {
        if (!this.directory.entries.hasOwnProperty(entryId)) continue;

        var entry = this.directory.entries[entryId];

        for (var contactId in entry.contacts) {
            if (!entry.contacts.hasOwnProperty(contactId)) continue;

            for (propId in propArray[propArrayIndex]) {
                if (!propArray[propArrayIndex].hasOwnProperty(propId)) continue;
                entry.contacts[contactId][propId] = propArray[propArrayIndex][propId];
            }

            propArrayIndex++;
            if (propArrayIndex > propArray.length - 1) propArrayIndex = 0;
        }
    }
};

Simulator.prototype.makeParticipantDisplayName = function(contact) {
    if (!contact) return '';
    return (typeof contact.firstName !== 'undefined' ? contact.firstName : '') +
        (typeof contact.firstName !== 'undefined' &&
            typeof contact.lastName !== 'undefined' ? ' ' : '') +
        (typeof contact.lastName !== 'undefined' ? contact.lastName : '');
};

Simulator.prototype.onComputeCaps = function() {
    var caps = this.getCapsTemplate();
    var id;

    if (this.simulationParams.caps.hasAssistiveListening) {
        this.computeAssistiveListeningCaps(caps);
    }

    if (this.simulationParams.caps.hasCalendar && this.config.useCalendar) {
        caps.hasCalendar = true;
    }

    if (this.simulationParams.caps.hasDirectory && this.config.useDirectory) {
        caps.hasDirectory = true;
    }

    if (this.simulationParams.caps.hasFirmwareVersion) {
        caps.hasFirmwareVersion = true;
    }

    if (this.simulationParams.caps.hasRoomMute &&
        (this.config.hasOwnProperty('mics') ||
         this.configNotRequired('roomMute'))) {
        caps.hasRoomMute = true;
    }

    if (this.simulationParams.caps.hasRoomVolume &&
        (this.config.hasOwnProperty('amplifier') ||
         this.configNotRequired('roomVolume'))) {
        caps.hasRoomVolume = true;

        if (this.simulationParams.caps.hasRoomVolumeMute) {
            caps.hasRoomVolumeMute = true;
        }
    }

    if (this.config.hasOwnProperty('zoomConnectorEnabled')) {
        caps.hasZoomConnector = this.config.zoomConnectorEnabled;
    }

    if (typeof this.cameras !== 'undefined') {
        var i = 1;

        for (id in this.cameras) {
            if (!this.cameras.hasOwnProperty(id)) continue;

            caps.cameras[id] = {
                name: this.cameras[id].hasOwnProperty('name') ?
                    this.cameras[id].name : 'Camera ' + i ,
                hasCameraTracking: this.simulationParams.caps.hasCameraTracking,
                hasCameraTrackingUi: this.simulationParams.caps.hasCameraTracking &&
                    this.getNode(this.config, 'cameraTrackingUiEnabled', true),
                hasPan: true,
                hasTilt: true,
                hasZoom: true,
                location: 'near',
            };

            if (this.cameras[id].hasOwnProperty('extControl')) {
                caps.cameras[id].extControl = this.cameras[id].extControl;
            }

            i++;
        }
    }

    this.computeCameraPresetsCaps(caps);

    if (this.simulationParams.caps.hasPowerControl) {
        caps.hasPowerControl = true;
    }

    if (this.simulationParams.caps.hasAudioInputSourcesMute) {
        this.computeAudioInputSourceCaps(caps);
    }

    if (this.simulationParams.caps.hasOverflowRoomsMute) {
        this.computeOverflowRoomCaps(caps);
    }

    if (this.simulationParams.caps.hasSoundReinforcement) {
        this.computeSoundReinforcementCaps(caps);
    }

    // This must come before computeVideoInputs.

    if (this.simulationParams.caps.videoInputsAreContent) {
        caps.videoInputsAreContent = true;
        this.contentSource = this.noContentId;
    }

    if (this.getNode(this.simulationParams, [ 'videoInputs', 'dynamic' ])) {
        caps.hasDynamicVideoInputs = true;
    }

    if (this.simulationParams.caps.hasVideoSwitcher &&
        this.simulationParams.caps.hasVideoInputs) {
        caps.hasVideoSwitcher = true;

        if (this.simulationParams.caps.hasVideoOutputs) {
            this.computeVideoMatrixCaps(caps);
        } else {
            this.computeVideoInputsCaps(caps, this.videoInputsConfig);
        }
    }

    if (this.simulationParams.caps.videoInputsAreContent) {
        for (id in this.caps.videoInputs) {
            if (!this.caps.videoInputs.hasOwnProperty(id)) continue;

            if (this.contentStates && !this.contentStates.hasOwnProperty(id)) {
                this.contentStates[id] = null;
            }
        }

        for (id in this.contentStates) {
            if (!this.contentStates.hasOwnProperty(id)) continue;

            if (!this.caps.videoInputs.hasOwnProperty(id)) {
                delete this.contentStates[id];
            }
        }
    }

    var mustIncludes = this.getNode(this.simulationParams,
        [ 'videoInputs', 'mustIncludes' ]);

    if (mustIncludes) {
        mustIncludes = Object.keys(mustIncludes);
        caps.mustIncludes = [];

        for (id in caps.videoInputs) {
            if (!caps.videoInputs.hasOwnProperty(id)) continue;

            if (mustIncludes.includes(caps.videoInputs[id].code)) {
                caps.mustIncludes.push(id);
            }
        }
    }

    this.computeCapsMaxCalls(caps);

    if (this.simulationParams.caps.hasLayoutSwitcher) {
        caps.hasLayoutSwitcher = true;
    }

    if (this.simulationParams.caps.isPowerStateless) {
        caps.isPowerStateless = true;
    }

    if (this.simulationParams.caps.isVideoInputStateless) {
        caps.isVideoInputStateless = true;
    }

    if (this.simulationParams.caps.hasDisplayBlank) {
        caps.hasDisplayBlank = true;
        caps.hasDisplayBlankUi = this.getNode(this.config, 'displayBlankUiEnabled', true);
    }

    if (this.simulationParams.caps.isDisplayBlankStateless) {
        caps.isDisplayBlankStateless = true;
    }

    if (this.simulationParams.caps.hasDisplayFreeze) {
        caps.hasDisplayFreeze = true;
        caps.hasDisplayFreezeUi = this.getNode(this.config, 'displayFreezeUiEnabled', true);
    }

    if (this.simulationParams.caps.isDisplayFreezeStateless) {
        caps.isDisplayFreezeStateless = true;
    }

    return caps;
};

Simulator.prototype.onConnect = function() {
    this.resetCallInterfaces();
    this.finishConnecting();
};

Simulator.prototype.onInitialize = function() {
    if (this.simulationParams.hasOwnProperty('detected') &&
        (!this.simulationParams.hasOwnProperty('sendDetected') ||
         this.simulationParams.sendDetected)) {
        this.sendDetected(this.simulationParams.detected);
    }

    if (this.simulationParams.caps.hasLayoutSwitcher &&
        this.simulationParams.hasOwnProperty('layouts')) {

        this.layouts = {};

        for (var i = 0; i < this.simulationParams.layouts.length; i++) {
            var id = IdGenerator.generateId();
            this.layouts[id] = this.simulationParams.layouts[i];
            this.layoutSelections[id] = Object.keys(this.layouts[id])[0];
        }

        this.broadcastStatus('layouts', this.layouts);
        this.broadcastStatus('layout', this.layoutSelections);
    }

    if (this.simulationParams.caps.videoInputsAreContent) {
        this.contentStates = {};
    }

    if (this.simulationParams.hasOwnProperty('equipmentSettings')) {
        this.states = {};

        for (var name in this.simulationParams.equipmentSettings) {
            if (!this.simulationParams.equipmentSettings.hasOwnProperty(name)) continue;
            var s = this.simulationParams.equipmentSettings[name];
            if (s.hasOwnProperty('stateName') &&
                s.hasOwnProperty('initialValue')) {
                this.states[s.stateName] = s.initialValue;
            }
        }

        this.sendStates(this.states);
    }

    this.sharingOnlyMeetings = {};
    this.finishInitialization();
};

Simulator.prototype.onUpdateConfig = function() {
    var i;
    var ci;
    var id;
    var newId;
    var srcKeys;
    var firstCamera = true;
    var dynamicVideoInputs;

    this.cameras = {};

    CallsDevice.prototype.onUpdateConfig.call(this);

    for (i = 0; i < this.callInterfaces.length; i++) {
        ci = this.callInterfaces[i];
        ci.callType = this.simulationParams.getTelephonyType(ci, this.simulationParams.detected);

        if (this.simulationParams.callHasReceiveGain(ci, this.config, this.simulationParams.detected)) {
            ci.receiveGainValue = 0;
        }
    }

    if (this.simulationParams.caps.hasOwnProperty('maxCalls')) {
        if (this.simulationParams.caps.maxCalls.hasOwnProperty('zoom')) {
            for (i = 0; i < this.simulationParams.caps.maxCalls.zoom; i++) {
                this.callInterfaces.push({
                    configId: 'z' + i,
                    name: 'zoom' + i,
                    callType: 'zoom',
                });
            }
        }

        if (this.simulationParams.caps.maxCalls.hasOwnProperty('video')) {
            for (i = 0; i < this.simulationParams.caps.maxCalls.video; i++) {
                this.callInterfaces.push({
                    configId: 'v' + i,
                    name: 'video' + i,
                    callType: 'video',
                });
            }
        }
    }

    this.videoInputsConfig = null;

    dynamicVideoInputs = this.getNode(this.simulationParams,
        [ 'videoInputs', 'dynamic' ]);

    if (dynamicVideoInputs !== null) {
        this.videoInputsConfig = {};

        dynamicVideoInputs = JSON.parse(JSON.stringify(dynamicVideoInputs));
        for (i = 0; i < dynamicVideoInputs.length; i++) {
            if (dynamicVideoInputs[i].code === 'airPlay' &&
                !this.getNode(this.config, 'airPlayContentEnabled', true)) continue;

            if (dynamicVideoInputs[i].hasOwnProperty('name')) {
                dynamicVideoInputs[i].name = this.getTranslation(dynamicVideoInputs[i].name);
            }
            if (dynamicVideoInputs[i].hasOwnProperty('description')) {
                dynamicVideoInputs[i].description = this.getTranslation(dynamicVideoInputs[i].description);
            }
            this.videoInputsConfig[this.generateId()] = dynamicVideoInputs[i];
        }
    } else {
        this.videoInputsConfig = this.getNode(this.config, [ 'videoInputs' ]);
    }

    if (this.videoInputsConfig !== null) {
        if (this.config.hasOwnProperty('videoOutputs')) {
            var inSrcKeys = Object.keys(this.videoInputsConfig).sort();
            var outId;

            for (outId in this.config.videoOutputs) {
                if (!this.config.videoOutputs.hasOwnProperty(outId)) continue;

                if (!this.videoMatrixRoutes.hasOwnProperty[outId] ||
                    this.videoMatrixRoutes[outId] === null ||
                    !this.videoInputsConfig.hasOwnProperty(this.videoMatrixRoutes[outId])) {
                    this.videoMatrixRoutes[outId] = inSrcKeys[0];
                }
            }

            for (outId in this.videoMatrixRoutes) {
                if (!this.videoMatrixRoutes.hasOwnProperty(outId)) continue;

                if (!this.config.videoOutputs.hasOwnProperty(outId)) {
                    delete this.videoMatrixRoutes[outId];
                }
            }
        } else if (this.simulationParams.videoInputsAreContent) {
            // We can't set this to noContentId here because noContentId is
            // computed in computeCaps, which is called after this function; so
            // we set it in onComputeCaps instead.

            this.contentSource = null;
        } else {
            if (this.videoInput === null ||
                !this.videoInputsConfig.hasOwnProperty(this.videoInput)) {
                srcKeys = Object.keys(this.videoInputsConfig).sort();
                this.videoInput = srcKeys[0];
            }
        }
    }

    if (this.simulationParams.caps.hasOwnProperty('numCameras') &&
        this.simulationParams.caps.numCameras > 0) {
        for (i = 0; i < this.simulationParams.caps.numCameras; i++) {
            id = IdGenerator.generateId();
            this.cameras[id] = {
                position: { x: 0, y: 0, z: 0},
                tracking: true,
                selected: i === 0,
            };
        }
    }

    if (this.simulationParams.caps.useAllDetectedCameras) { // Zoom
        for (id in this.simulationParams.detected) {
            if (!this.simulationParams.detected.hasOwnProperty(id)) continue;
            var det = this.simulationParams.detected[id];
            if (det.filters.camera) {
                newId = IdGenerator.generateId();
                this.cameras[newId] = {
                    name: det.name,
                    position: { x: 0, y: 0, z: 0 },
                    tracking: false,
                    selected: firstCamera,
                };

                firstCamera = false;

                for (var cameraId in this.config.cameras) {
                    if (!this.config.cameras.hasOwnProperty(cameraId)) continue;
                    if (this.config.cameras[cameraId].zoomId === det.zoomId) {
                        if (this.config.cameras[cameraId].hasOwnProperty('name')) {
                            this.cameras[newId].name = this.config.cameras[cameraId].name;
                        }

                        if (this.config.cameras[cameraId].hasOwnProperty('extControl')) {
                            this.cameras[newId].extControl = {
                                move: true,
                                tracking: false,
                                preset: false,
                                equipmentId: this.config.cameras[cameraId].extControl.equipmentId,
                                configId: this.config.cameras[cameraId].extControl.configId,
                            };
                        }
                    }
                }
            }
        }
    } else { // e.g., VISCA camera
        for (id in this.config.cameras) {
            if (!this.config.cameras.hasOwnProperty(id)) continue;
            newId = IdGenerator.generateId();
            this.cameras[newId] = {
                name: this.config.cameras[id].name,
                position: { x: 0, y: 0, z: 0 },
                tracking: false,
                selected: firstCamera,
                configId: id,
                viscaId: this.config.cameras[id].viscaId,
            };

            firstCamera = false;
        }
    }
};

Simulator.prototype.onUpdateStatesTimeout = function() {
    this.updateStatesTimeout = null;

    if (this.updateStatesAfterTimeout) {
        if (this.fsm.current === 'controlling') {
            this.sendStates(this.states);
        }
        this.updateStatesAfterTimeout = false;
    }
};

Simulator.prototype.onValidate = function() {
    var roomId, zoneId, srcId, telId, i;

    if (!this.simulationParams.hasOwnProperty('detected')) {
        this.finishValidation();
        return;
    }

    var validated = {};
    var valid;
    var cfg;

    if (this.simulationParams.caps.hasRoomMute &&
        this.config.hasOwnProperty('mics')) {
        validated.mics = this.validateMuteConfig(this.config.mics, 'mics');
    }

    if (this.simulationParams.caps.hasRoomVolume &&
        this.config.hasOwnProperty('amplifier')) {
        validated.amplifier = this.validateEntry(this.config.amplifier, 'amplifier');
    }

    if (this.simulationParams.caps.hasAssistiveListening &&
        this.config.hasOwnProperty('assistiveListening')) {
        cfg = this.config.assistiveListening;

        if (!cfg.hasOwnProperty('mute') && !cfg.hasOwnProperty('volume')) {
            log.error(this + ': invalid assistive listening schema'); // before SNTY-1597
            validated.assistiveListening = {
                mute: 'invalidChannel'
            };
        } else { // new schema, after SNTY-1597
            if (cfg.hasOwnProperty('mute')) {
                this.setNode(validated, 'assistiveListening.mute',
                    this.validateMuteConfig(cfg.mute, 'assistiveListeningMute'));
            }

            if (cfg.hasOwnProperty('volume')) {
                this.setNode(validated, 'assistiveListening.volume',
                    this.validateEntry(cfg.volume, 'assistiveListeningVolume'));
            }
        }
    }

    if (this.simulationParams.caps.hasAudioInputSourcesMute &&
        this.config.hasOwnProperty('audioInputSources')) {

        for (srcId in this.config.audioInputSources) {
            if (!this.config.audioInputSources.hasOwnProperty(srcId)) continue;
            cfg = this.config.audioInputSources[srcId];

            if (cfg.hasOwnProperty('mute')) {
                valid = this.validateMuteConfig(cfg.mute, 'audioInputMute');

                if (valid === 'ok' &&
                    this.duplicateExists(srcId, this.config.audioInputSources, 'mute')) {
                    valid = 'duplicateChannel';
                }

                this.setNode(validated, [ 'audioInputSources', srcId, 'mute' ], valid);
            }

            if (cfg.hasOwnProperty('volume')) {
                valid = this.validateEntry(cfg.volume, 'audioInputVolume');

                if (valid === 'ok' &&
                    this.duplicateExists(srcId, this.config.audioInputSources, 'volume')) {
                    valid = 'duplicateChannel';
                }

                this.setNode(validated, [ 'audioInputSources', srcId, 'volume' ], valid);
            }
        }
    }

    if (this.simulationParams.caps.hasOverflowRoomsMute &&
        this.config.hasOwnProperty('overflowRooms')) {

        for (roomId in this.config.overflowRooms) {
            if (!this.config.overflowRooms.hasOwnProperty(roomId)) continue;
            cfg = this.config.overflowRooms[roomId];

            if (cfg.hasOwnProperty('mute')) {
                valid = this.validateMuteConfig(cfg.mute, 'overflowRoomMute');

                if (valid === 'ok' &&
                    this.duplicateExists(roomId, this.config.overflowRooms, 'mute')) {
                    valid = 'duplicateChannel';
                }

                this.setNode(validated, [ 'overflowRooms', roomId, 'mute' ], valid);
            }

            if (cfg.hasOwnProperty('volume')) {
                valid = this.validateEntry(cfg.volume, 'overflowRoomVolume');

                if (valid === 'ok' &&
                    this.duplicateExists(roomId, this.config.overflowRooms, 'volume')) {
                    valid = 'duplicateChannel';
                }

                this.setNode(validated, [ 'overflowRooms', roomId, 'volume' ], valid);
            }
        }
    }

    if (this.simulationParams.caps.hasSoundReinforcement &&
        this.config.hasOwnProperty('soundReinforcement')) {

        for (zoneId in this.config.soundReinforcement) {
            if (!this.config.soundReinforcement.hasOwnProperty(zoneId)) continue;
            cfg = this.config.soundReinforcement[zoneId];

            if (cfg.hasOwnProperty('mute')) {
                valid = this.validateMuteConfig(cfg.mute, 'soundReinforcementMute');

                if (valid === 'ok' &&
                    this.duplicateExists(zoneId, this.config.soundReinforcement, 'mute')) {
                    valid = 'duplicateChannel';
                }

                this.setNode(validated, [ 'soundReinforcement', zoneId, 'mute' ], valid);
            }

            if (cfg.hasOwnProperty('volume')) {
                valid = this.validateEntry(cfg.volume, 'soundReinforcementVolume');

                if (valid === 'ok' &&
                    this.duplicateExists(zoneId, this.config.soundReinforcement, 'volume')) {
                    valid = 'duplicateChannel';
                }

                this.setNode(validated, [ 'soundReinforcement', zoneId, 'volume' ], valid);
            }
        }
    }

    if (this.config.hasOwnProperty('telephony')) {
        validated.telephony = {};

        for (telId in this.config.telephony) {
            if (!this.config.telephony.hasOwnProperty(telId)) continue;

            if (this.simulationParams.hasOwnProperty('telephonyChannels')) {
                validated.telephony[telId] = {};

                for (i = 0; i < this.simulationParams.telephonyChannels.length; i++) {
                    var info = this.simulationParams.telephonyChannels[i];
                    cfg = this.config.telephony[telId];

                    if (cfg.hasOwnProperty(info.name)) {
                        validated.telephony[telId][info.name] = this.validateEntry(
                            this.config.telephony[telId][info.name], info.filter, info.name);

                        if (validated.telephony[telId][info.name] === 'ok' &&
                            this.duplicateExists(telId, this.config.telephony, info.name)) {
                            validated.telephony[telId][info.name] = 'duplicateChannel';
                        }
                    }
                }
            } else {
                validated.telephony[telId] = this.validateEntry(
                    this.config.telephony[telId], 'telephony');

                if (validated.telephony[telId] === 'ok' &&
                    this.duplicateExists(telId, this.config.telephony)) {
                    validated.telephony[telId] = 'duplicateChannel';
                }
            }
        }
    }

    if (this.config.hasOwnProperty('cameras')) {
        validated.cameras = {};

        for (var cameraId in this.config.cameras) {
            if (!this.config.cameras.hasOwnProperty(cameraId)) continue;
            validated.cameras[cameraId] = this.validateEntry(
                this.config.cameras[cameraId], 'camera');
            if (validated.cameras[cameraId] === 'ok' &&
                this.duplicateExists(cameraId, this.config.cameras)) {
                validated.cameras[cameraId] = 'duplicateChannel';
            }
        }
    }

    if (this.config.hasOwnProperty('audioInputSources')) {
        var newAudioInputSources = {};

        for (srcId in this.config.audioInputSources) {
            if (!this.config.audioInputSources.hasOwnProperty(srcId)) continue;

            newAudioInputSources[srcId] = {};

            if (this.config.audioInputSources[srcId].hasOwnProperty('mute')) {
                if (this.audioInputSources.hasOwnProperty(srcId)) {
                    newAudioInputSources[srcId].mute = this.audioInputSources[srcId].mute;
                } else {
                    newAudioInputSources[srcId].mute = true;
                }
            }

            if (this.config.audioInputSources[srcId].hasOwnProperty('volume')) {
                if (this.audioInputSources.hasOwnProperty(srcId)) {
                    newAudioInputSources[srcId].volume = this.audioInputSources[srcId].volume;
                } else {
                    newAudioInputSources[srcId].volume = 0;
                }
            }
        }

        this.audioInputSources = newAudioInputSources;
    }

    if (this.config.hasOwnProperty('overflowRooms')) {
        var newOverflowRooms = {};

        for (roomId in this.config.overflowRooms) {
            if (!this.config.overflowRooms.hasOwnProperty(roomId)) continue;

            newOverflowRooms[roomId] = {};

            if (this.config.overflowRooms[roomId].hasOwnProperty('mute')) {
                if (this.overflowRooms.hasOwnProperty(roomId)) {
                    newOverflowRooms[roomId].mute = this.overflowRooms[roomId].mute;
                } else {
                    newOverflowRooms[roomId].mute = false;
                }
            }

            if (this.config.overflowRooms[roomId].hasOwnProperty('volume')) {
                if (this.overflowRooms.hasOwnProperty(roomId)) {
                    newOverflowRooms[roomId].volume = this.overflowRooms[roomId].volume;
                } else {
                    newOverflowRooms[roomId].volume = 0;
                }
            }
        }

        this.overflowRooms = newOverflowRooms;
    }

    if (this.config.hasOwnProperty('soundReinforcement')) {
        var newSoundReinforcementZones = {};

        for (zoneId in this.config.soundReinforcement) {
            if (!this.config.soundReinforcement.hasOwnProperty(zoneId)) continue;

            newSoundReinforcementZones[zoneId] = {};

            if (this.config.soundReinforcement[zoneId].hasOwnProperty('mute')) {
                if (this.soundReinforcementZones.hasOwnProperty(zoneId)) {
                    newSoundReinforcementZones[zoneId].mute = this.soundReinforcementZones[zoneId].mute;
                } else {
                    newSoundReinforcementZones[zoneId].mute = false;
                }
            }

            if (this.config.soundReinforcement[zoneId].hasOwnProperty('volume')) {
                if (this.soundReinforcementZones.hasOwnProperty(zoneId)) {
                    newSoundReinforcementZones[zoneId].volume = this.soundReinforcementZones[zoneId].volume;
                } else {
                    newSoundReinforcementZones[zoneId].volume = 0;
                }
            }
        }

        this.soundReinforcementZones = newSoundReinforcementZones;
    }

    this.finishValidation(validated);
};

Simulator.prototype.startContent = function(srcId) {
    for (var id in this.contentStates) {
        if (!this.contentStates.hasOwnProperty(id)) continue;

        var changed = false;

        if (id === srcId && srcId !== this.noContentId) {
            if (this.contentStates[id] !== 'sending') {
                this.contentStates[id] = 'sending';
                changed = true;
            }
        } else {
            if (this.contentStates[id] !== null) {
                this.contentStates[id] = null;
                changed = true;
            }
        }

        if (changed) {
            this.broadcastStatus('contentState', {
                id: id,
                state: this.contentStates[id],
            });
        }
    }
};

Simulator.prototype.stopContent = function(srcId) {
    if (typeof srcId === 'undefined') {
        for (var id in this.contentStates) {
            if (!this.contentStates.hasOwnProperty(id)) continue;

            if (this.contentStates[id] !== null) {
                this.contentStates[id] = null;
                this.broadcastStatus('contentState', {
                    id: id,
                    state: this.contentStates[id],
                });
            }
        }
    } else {
        if (!this.contentStates.hasOwnProperty(srcId)) {
            log.error(this + ': stopContent: invalid srcId ' + srcId);
            return;
        }

        if (this.contentStates[srcId] !== null) {
            this.contentStates[srcId] = null;
            this.broadcastStatus('contentState', {
                id: srcId,
                state: this.contentStates[srcId],
            });
        }
    }

    this.contentSource = this.noContentId;
    this.broadcastStatus('contentSource', this.contentSource);

    var names = Object.keys(this.sharingOnlyMeetings);

    for (var i = 0; i < names.length; i++) {
        var name = names[i];
        var ci = this.getCallInterfaceByName(name);
        this.disconnectCall(ci);
        delete this.sharingOnlyMeetings[name];
    }
};

Simulator.prototype.updateStatesDeferred = function() {
    if (this.updateStatesTimeout === null) {
        this.sendStates(this.states);
        this.updateStatesTimeout = window.setTimeout(
            this.onUpdateStatesTimeout.bind(this), 2000);
    } else {
        this.updateStatesAfterTimeout = true;
    }
};

Simulator.prototype.updateStatesIfNecessary = function(cmdName, value) {
    if (!this.simulationParams.hasOwnProperty('equipmentSettings')) return;

    var s;
    var found = false;

    for (var name in this.simulationParams.equipmentSettings) {
        if (!this.simulationParams.equipmentSettings.hasOwnProperty(name)) continue;
        s = this.simulationParams.equipmentSettings[name];
        if (s.hasOwnProperty('cmdName') &&
            s.cmdName === cmdName) {
                found = true;
                break;
        }
    }

    if (!found) return;

    value = s.convertFromCommand.call(this, value);
    if (value === this.states[s.stateName]) return;
    this.states[s.stateName] = value;
    this.updateStatesDeferred();
};

Simulator.prototype.validateEntry = function(cfg, filter, name) {
    for (var key in this.simulationParams.detected) {
        if (!this.simulationParams.detected.hasOwnProperty(key)) continue;
        var det = this.simulationParams.detected[key];

        if (this.simulationParams.channelMatchesDetected(cfg, det, name, filter)) {
            if (this.getNode(det, ['filters', filter], false)) {
                return 'ok';
            } else {
                return 'invalidChannel';
            }
        }
    }

    return 'noChannel';
};

Simulator.prototype.validateMuteConfig = function(cfg, filter, name) {
    if (typeof cfg === 'object' && cfg.hasOwnProperty('mutePreset')) {
        var mutePresetValidated;
        var unmutePresetValidated;

        if (this.getNode(this.simulationParams, ['validatePresets'], true)) {
            mutePresetValidated = this.validateEntry(cfg.mutePreset, 'preset');
            unmutePresetValidated = this.validateEntry(cfg.unmutePreset, 'preset');
        } else {
            mutePresetValidated = 'ok';
            unmutePresetValidated = 'ok';
        }

        var muteStatusValidated = this.validateEntry(cfg.muteStatus, filter);

        if (mutePresetValidated === 'noChannel' ||
            unmutePresetValidated === 'noChannel' ||
            muteStatusValidated === 'noChannel') {
            return 'noChannel';
        }

        if (mutePresetValidated === 'invalidChannel' ||
            unmutePresetValidated === 'invalidChannel' ||
            muteStatusValidated === 'invalidChannel') {
            return 'invalidChannel';
        }

        return 'ok';
    }

    return this.validateEntry(cfg, filter, name);
};

module.exports = Simulator;
